import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../lib/api';
import './LeaveAdminList.css'; 
import { useNavigate } from 'react-router-dom';

const REMAIN_OPTIONS = [
    { value: 'all',  label: 'すべて' },
    { value: 'low',  label: '残10日以下' },
    { value: 'zero', label: '残0日（消化済み）' },
];

/** 会計年度開始月から12ヶ月分の月番号配列を生成 */
const getFiscalMonths = (startMonth) => {
    const months = [];
    for (let i = 0; i < 12; i++) {
        months.push(((startMonth - 1 + i) % 12) + 1);
    }
    return months;
};

/** 月が現在まだ終わっていないか判定（承認済み有給の赤色表示用） */
const isMonthNotEnded = (month, fiscalYear, fiscalStartMonth) => {
    const today = new Date();
    const year = month >= fiscalStartMonth ? fiscalYear : fiscalYear + 1;
    const lastDayOfMonth = new Date(year, month, 0); 
    return today <= lastDayOfMonth;
};

const LeaveAdminList = () => {
    const [leaveData, setLeaveData]           = useState([]);
    const [employees, setEmployees]           = useState([]);
    const [departments, setDepartments]       = useState([]);
    const [loading, setLoading]               = useState(false);
    const [error, setError]                   = useState(null);
    const [empSearch, setEmpSearch]           = useState('');
    const [selectedMember, setSelectedMember] = useState('');
    const [selectedDept, setSelectedDept]     = useState('');   // 部名
    const [selectedKaName, setSelectedKaName] = useState('');  // 課名
    const [remainFilter, setRemainFilter]     = useState('all');
    const [showEmpSuggest, setShowEmpSuggest] = useState(false);
    const [uploading, setUploading]           = useState(false);

    // Danh sách năm động lấy từ hệ thống (Dynamic Fiscal Years)
    const [availableYears, setAvailableYears] = useState([]);

    // 有給付与計算モーダル
    const [showGrantModal, setShowGrantModal]   = useState(false);
    const [grantAsOf, setGrantAsOf]             = useState('');
    const [grantCarryOver, setGrantCarryOver]   = useState(true);   
    const [grantDryRun, setGrantDryRun]         = useState(true);
    const [grantLoading, setGrantLoading]       = useState(false);
    const [grantResult, setGrantResult]         = useState(null);
    const [grantError, setGrantError]           = useState(null);

    const navigate = useNavigate();

    // 現在の会計年度（デフォルト：4月始まり）
    const getDefaultFiscalYear = () => {
        const today = new Date();
        return today.getMonth() + 1 >= 4 ? today.getFullYear() : today.getFullYear() - 1;
    };
    const [fiscalYear, setFiscalYear] = useState(getDefaultFiscalYear);
    const [fiscalStartMonth, setFiscalStartMonth] = useState(4);
    const currentFiscalMonths = useMemo(() => getFiscalMonths(fiscalStartMonth), [fiscalStartMonth]);

   
    const deptOptions = useMemo(() => [
        { value: '', label: 'すべての部署' },
        ...departments
            .filter(d => d.departmentId && d.departmentId.endsWith('00') && !d.departmentId.startsWith('00'))
            .sort((a, b) => (a.departmentName || '').localeCompare(b.departmentName || ''))
            .map(d => ({ value: d.departmentName, label: d.departmentName })),
    ], [departments]);

    /** 選択中の部署オブジェクト */
    const selectedDeptObj = useMemo(
        () => departments.find(d => d.departmentName === selectedDept) || null,
        [departments, selectedDept]
    );

    /** 課の候補。/api/departments/ は「部（ルート）＋ children（課）」のツリーを返すので、
     *  選択中の部の children をそのまま使う（departmentId の命名規約から探すと必ず空になる）。 */
    const kaOptions = useMemo(() => {
        const children = selectedDeptObj?.children || [];
        return [
            { value: '', label: '課を選択（任意）' },
            ...children
                .filter(c => c.departmentName)
                .sort((a, b) => (a.departmentName || '').localeCompare(b.departmentName || ''))
                .map(c => ({ value: c.departmentName, label: c.departmentName })),
        ];
    }, [selectedDeptObj]);

    const handleCsvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        setUploading(true);

        try {
            // Content-Type は指定しない（axios/ブラウザが boundary 付きで自動設定する）
            await api.post('/admin/leave/master/import-csv/', formData);
            alert("CSVのインポートに成功しました");
            fetchData(selectedMember, selectedKaName || selectedDept, remainFilter, fiscalYear, fiscalStartMonth); 
        } catch (err) {
            alert("インポート失敗: " + (err.response?.data?.error || "サーバーエラー"));
        } finally {
            setUploading(false);
            e.target.value = ''; 
        }
    };

    
    useEffect(() => {
        const loadMasters = async () => {
            try {
                const [empRes, deptRes, masterLeaveRes] = await Promise.all([
                    api.get('/employees/', { params: { page_size: 1000 } }),
                    api.get('/departments/'),
                    api.get('/admin/leave/master/'), 
                ]);

                const empList = Array.isArray(empRes.data) ? empRes.data : (empRes.data?.results ?? []);
                setEmployees(empList);

                const deptList = Array.isArray(deptRes.data) ? deptRes.data : (deptRes.data?.results ?? []);
                setDepartments(deptList);

                
                const leaveRecords = Array.isArray(masterLeaveRes.data) ? masterLeaveRes.data : [];
                const yearsSet = new Set();
                leaveRecords.forEach(rec => {
                    if (rec.fiscalYear) yearsSet.add(Number(rec.fiscalYear));
                });
             
                const currentYear = new Date().getFullYear();
                yearsSet.add(currentYear - 1);
                yearsSet.add(currentYear);
                yearsSet.add(currentYear + 1);

                setAvailableYears(Array.from(yearsSet).sort((a, b) => a - b));
            } catch (err) {
                
                const currentYear = new Date().getFullYear();
                setAvailableYears([currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2]);
            }
        };
        loadMasters();
    }, []);

    // 有給データ取得
    const fetchData = useCallback(async (memberId, dept, remain, fy, fsm) => {
        setLoading(true);
        setError(null);
        try {
            const params = {};
            if (memberId) params.member_id = memberId;
            if (dept)     params.department = dept;
            if (remain && remain !== 'all') params.remain_filter = remain;
            params.fiscal_year = fy || fiscalYear;
            params.fiscal_start_month = fsm || fiscalStartMonth;

            const response = await api.get('/admin/leave/list/', { params });
            const data = response.data;
            setLeaveData(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('データの取得に失敗しました。');
            setLeaveData([]);
        } finally {
            setLoading(false);
        }
    }, [fiscalYear, fiscalStartMonth]);

    useEffect(() => {
        fetchData(selectedMember, selectedKaName || selectedDept, remainFilter, fiscalYear, fiscalStartMonth);
    }, [fetchData, fiscalYear, fiscalStartMonth]);

   
    const handleGrantExecute = async () => {
        setGrantLoading(true);
        setGrantError(null);
        setGrantResult(null);
        try {
            const res = await api.post('/admin/leave/grant/', {
                as_of:      grantAsOf || undefined,
                carry_over: grantCarryOver,
                dry_run:    grantDryRun,
            });
            setGrantResult(res.data);
            
           
            if (!grantDryRun) {
                fetchData(selectedMember, selectedKaName || selectedDept, remainFilter, fiscalYear, fiscalStartMonth);
            }
        } catch (err) {
            const msg = err?.response?.data?.error || '付与計算中にエラーが発生しました。';
            setGrantError(msg);
        } finally {
            setGrantLoading(false);
        }
    };

    const handleGrantConfirm = () => {
        if (!grantDryRun) {
            if (!window.confirm('有給付与日数を実際に更新します。よろしいですか？')) return;
        }
        handleGrantExecute();
    };

    // 社員オートコンプリート候補
    const empSuggestions = useMemo(() => {
        if (!empSearch.trim()) return [];
        const q = empSearch.toLowerCase();
        return employees
            .filter(e =>
                (e.name || '').toLowerCase().includes(q) ||
                (e.memberId || '').toLowerCase().includes(q)
            )
            .slice(0, 10);
    }, [empSearch, employees]);

    const handleEmpSearchChange = (e) => {
        setEmpSearch(e.target.value);
        setSelectedMember('');
        setShowEmpSuggest(true);
    };

    const handleSelectEmployee = (emp) => {
        setEmpSearch(`${emp.memberId}  ${emp.name}`);
        setSelectedMember(emp.memberId);
        setShowEmpSuggest(false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const deptParam = selectedKaName || selectedDept;
        fetchData(selectedMember, deptParam, remainFilter, fiscalYear, fiscalStartMonth);
    };

    const handleReset = () => {
        setEmpSearch('');
        setSelectedMember('');
        setSelectedDept('');
        setSelectedKaName('');
        setRemainFilter('all');
        fetchData('', '', 'all', fiscalYear, fiscalStartMonth);
    };

    const getRemainClass = (remain) => {
        if (remain <= 0)  return 'remain-zero';
        if (remain <= 10) return 'remain-low';
        return '';
    };

    return (
        <div className="leave-admin-container">
            <div className="leave-admin-header">
                <h2 className="leave-admin-title">有給休暇管理</h2>
                <div className="leave-admin-header-actions">
                    <button
                        type="button"
                        className="btn-grant-open"
                        onClick={() => { setShowGrantModal(true); setGrantResult(null); setGrantError(null); }}
                    >
                        有給付与計算
                    </button>
                    <label className="btn-csv-upload" style={{ cursor: 'pointer' }}>
                        CSVアップロード
                        <input type="file" accept=".csv" onChange={handleCsvUpload} style={{ display: 'none' }} />
                    </label>
                    <button type="button" onClick={() => navigate('/dashboard')} className="leave-admin-back-btn">
                        メニュー画面に戻る
                    </button>
                </div>
            </div>

         
            <div className="fiscal-selector">
                <label className="fiscal-label">会計年度</label>
                <select
                    className="fiscal-select"
                    value={fiscalYear}
                    onChange={e => setFiscalYear(Number(e.target.value))}
                >
                    {availableYears.map(y => (
                        <option key={y} value={y}>{y}年度</option>
                    ))}
                </select>
                <label className="fiscal-label">会計開始月</label>
                <select
                    className="fiscal-select"
                    value={fiscalStartMonth}
                    onChange={e => setFiscalStartMonth(Number(e.target.value))}
                >
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <option key={m} value={m}>{m}月始まり</option>
                    ))}
                </select>
            </div>

            {/* 付与計算モーダル */}
            {showGrantModal && (
                <div className="modal-overlay" onClick={() => setShowGrantModal(false)}>
                    <div className="modal-box" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>有給休暇 自動付与計算</h3>
                            <button className="modal-close" onClick={() => setShowGrantModal(false)}>×</button>
                        </div>

                        <div className="modal-body">
                            <div className="grant-form">
                                <div className="grant-field">
                                    <label>基準日</label>
                                    <input
                                        type="date"
                                        value={grantAsOf}
                                        onChange={e => setGrantAsOf(e.target.value)}
                                        className="filter-input"
                                    />
                                    <small>省略すると今日の日付で計算します</small>
                                </div>
                                <div className="grant-field">
                                    <label>更新モード</label>
                                    <select
                                        className="filter-select"
                                        value={grantCarryOver ? 'carry' : 'overwrite'}
                                        onChange={e => setGrantCarryOver(e.target.value === 'carry')}
                                    >
                                        <option value="carry">繰り越しあり（推奨）— 2年時効を考慮して前年未消化分を繰り越す</option>
                                        <option value="overwrite">上書き — 勤続に応じた付与日数のみ設定（繰り越しなし）</option>
                                    </select>
                                    {grantCarryOver && (
                                        <small className="carry-note">
                                            ⚖ 労基法第115条：前年未消化分を前年付与日数を上限として繰り越します
                                        </small>
                                    )}
                                </div>
                                <div className="grant-field grant-field-check">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={grantDryRun}
                                            onChange={e => setGrantDryRun(e.target.checked)}
                                        />
                                        プレビューのみ（保存しない）
                                    </label>
                                </div>
                            </div>

                            {grantLoading && (
                                <div className="status-message loading-message">
                                    <span className="spinner" />計算中...
                                </div>
                            )}
                            {grantError && (
                                <div className="status-message error-message">
                                    <span className="error-icon">⚠</span>{grantError}
                                </div>
                            )}

                            {grantResult && (
                                <div className="grant-result">
                                    <div className={`grant-result-header ${grantResult.dry_run ? 'dry' : 'applied'}`}>
                                        {grantResult.dry_run
                                            ? `プレビュー（${grantResult.as_of} 基準）— ${grantResult.updated} 件対象`
                                            : `✓ 付与完了（${grantResult.as_of} 基準）— ${grantResult.updated} 件更新`
                                        }
                                    </div>
                                    {grantResult.results.length > 0 && (
                                        <div className="grant-result-scroll">
                                            <table className="grant-result-table">
                                                <thead>
                                                    <tr>
                                                        <th>社員コード</th>
                                                        <th>氏名</th>
                                                        <th>部署</th>
                                                        <th>勤続年数</th>
                                                        <th>今年付与</th>
                                                        {grantCarryOver && <th>繰り越し</th>}
                                                        <th>更新前</th>
                                                        <th>更新後</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {grantResult.results.map(r => (
                                                        <tr key={r.memberId}>
                                                            <td>{r.memberId}</td>
                                                            <td>{r.name}</td>
                                                            <td>{r.departmentName || '―'}</td>
                                                            <td className="num-cell">{r.serviceYears}年</td>
                                                            <td className="num-cell grant-days">{r.grantDays}日</td>
                                                            {grantCarryOver && (
                                                                <td className="num-cell carry-cell">+{r.carryOver ?? 0}日</td>
                                                            )}
                                                            <td className="num-cell">{r.before}</td>
                                                            <td className="num-cell grant-after">{r.after}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className={`btn-grant-exec ${grantDryRun ? 'dry' : 'apply'}`}
                                onClick={handleGrantConfirm}
                                disabled={grantLoading}
                            >
                                {grantDryRun ? 'プレビュー実行' : '付与を実行する'}
                            </button>
                            <button type="button" className="btn-reset" onClick={() => setShowGrantModal(false)}>
                                閉じる
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 検索フォーム */}
            <form onSubmit={handleSearch} className="leave-filter-form">
                <div className="filter-group autocomplete-wrapper">
                    <label className="filter-label">社員</label>
                    <div className="autocomplete-inner">
                        <input
                            type="text"
                            className="filter-input"
                            placeholder="社員名または社員コードで絞り込み"
                            value={empSearch}
                            onChange={handleEmpSearchChange}
                            onFocus={() => setShowEmpSuggest(true)}
                            onBlur={() => setTimeout(() => setShowEmpSuggest(false), 150)}
                            autoComplete="off"
                        />
                        {showEmpSuggest && empSuggestions.length > 0 && (
                            <ul className="autocomplete-list">
                                {empSuggestions.map(emp => (
                                    <li
                                        key={emp.memberId}
                                        onMouseDown={() => handleSelectEmployee(emp)}
                                        className="autocomplete-item"
                                    >
                                        <span className="ac-code">{emp.memberId}</span>
                                        <span className="ac-name">{emp.name}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="filter-group">
                    <label className="filter-label">部署</label>
                    <select
                        className="filter-select"
                        value={selectedDept}
                        onChange={e => {
                            setSelectedDept(e.target.value);
                            setSelectedKaName('');
                        }}
                    >
                        {deptOptions.map(o => (
                            <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label className="filter-label">課</label>
                    <select
                        className="filter-select"
                        value={selectedKaName}
                        onChange={e => setSelectedKaName(e.target.value)}
                        disabled={!selectedDept}
                    >
                        {kaOptions.map(o => (
                            <option key={o.value || 'ka-all'} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label className="filter-label">残日数</label>
                    <select
                        className="filter-select"
                        value={remainFilter}
                        onChange={e => setRemainFilter(e.target.value)}
                    >
                        {REMAIN_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-actions">
                    <button type="submit" className="btn-search">検索</button>
                    <button type="button" className="btn-reset" onClick={handleReset}>リセット</button>
                </div>
            </form>

            {/* テーブル */}
            {!loading && !error && (
                <>
                    <div className="result-summary">
                        {leaveData.length > 0 ? `${leaveData.length} 件表示` : ''}
                    </div>
                    <div className="table-wrapper">
                        <table className="leave-admin-table">
                            <thead>
                                <tr>
                                    <th>社員コード</th>
                                    <th>氏名</th>
                                    <th>入社年月日</th>
                                    <th>部署</th>
                                    <th>課</th>
                                    <th>前年付与</th>
                                    <th>本年付与</th>
                                    <th>前年消化</th>
                                    {currentFiscalMonths.map(month => (
                                        <th key={month}>{month}月</th>
                                    ))}
                                    <th>消化計</th>
                                    <th>残日数</th>
                                </tr>
                            </thead>
                            <tbody>
                                {leaveData.length > 0 ? (
                                    leaveData.map(item => {
                                        // 部署（部）と課はサーバが Department.parent から解決済みの値を返す。
                                        // departmentId の命名規約から推測し直すと課が常に空になるため、そのまま表示する。
                                        const displayBuName = item.departmentName || '―';
                                        const displayKaName = item.sectionName || '―';

                                        return (
                                            <tr key={item.memberId}>
                                                <td>{item.memberId}</td>
                                                <td>{item.name}</td>
                                                <td>{item.startDate}</td>
                                                <td>{displayBuName}</td>
                                                <td>{displayKaName}</td>
                                                <td className="num-cell">{item.prevYearEntitled}</td>
                                                <td className="num-cell">{item.currYearEntitled}</td>
                                                <td className="num-cell">{item.prevYearUsed}</td>
                                                {currentFiscalMonths.map(month => {
                                                    const val = item.monthlyDetails?.[month] ?? 0;
                                                    const isRed = val > 0 && isMonthNotEnded(month, fiscalYear, fiscalStartMonth);
                                                    return (
                                                        <td
                                                            key={month}
                                                            className="num-cell"
                                                            style={{ color: isRed ? '#e53935' : undefined, fontWeight: isRed ? 'bold' : undefined }}
                                                        >
                                                            {val}
                                                        </td>
                                                    );
                                                })}
                                                <td className="num-cell">{item.totalUsed}</td>
                                                <td className={`num-cell remain-cell ${getRemainClass(item.leaveRemain)}`}>
                                                    {item.leaveRemain ?? 0}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={9 + currentFiscalMonths.length} className="empty-message">
                                            データがありません
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default LeaveAdminList;